import axios from "axios";
import config from "@/config/constants";

// CR-300 guest-join contract, confirmed against the real QA environment via
// Postman (2026-09-14) — response fields are snake_case, matching the rest of
// this BE (see api/u/dmh/get-appointment-access-token's res.access_token in
// VideoRoom.vue). Called directly via axios (not the `api()` mixin) — there
// is no user_id to inject and no session for a "3994" expiry response to log
// out of.
//
// Error copy (attempts remaining, lockout, expiry, rate-limiting) is baked
// into the `err` string by the backend, not a separate structured field —
// display it verbatim rather than re-deriving it.

export type GuestJoinStartResponse = {
  success: boolean;
  // Absent (not "unavailable") when the feature flag itself is off. /start only
  // checks the join window now — it no longer sends the SMS itself, so
  // "otp_required" carries no challenge_token; that's send-code's job.
  state?: "otp_required" | "too_early" | "unavailable";
  phone_last4?: string;
  appointment_start?: string; // "YYYY-MM-DD HH:mm:ss", UTC, no offset marker
  clinician_first_name?: string | null;
};

export type GuestJoinSendCodeResponse = {
  success: boolean;
  err?: string;
  state?: "otp_sent";
  phone_last4?: string;
  appointment_start?: string;
  clinician_first_name?: string | null;
  challenge_token?: string;
};

export type GuestJoinVerifyResponse = {
  success: boolean;
  err?: string;
  locked?: boolean;
  // Present (non-null) only in the rare case the room was already open at
  // verify time — usually null even on success, since the clinician is
  // typically not in the room yet. Not relied on: GuestVideoRoom.vue's
  // tokenProvider always re-fetches via /token instead.
  access_token?: string | null;
  guest_session_token?: string;
  room_ready: boolean;
};

export type GuestJoinResendResponse = {
  success: boolean;
  err?: string;
};

export type GuestJoinTokenResponse = {
  success: boolean;
  // false with no other signal (no err field) means the guest_session_token
  // is unknown/expired — fall back to the OTP screen.
  access_token?: string | null;
  room_ready: boolean;
};

const GUEST_JOIN_BASE_PATH = "api/p/dmh/guest-join";

const post = async <T>(
  path: string,
  payload: Record<string, unknown>,
): Promise<T> => {
  const response = await axios.post(
    `${config.api_env}/application/${path}`,
    payload,
    config.options,
  );
  return response.data;
};

export const startGuestJoin = (
  token: string,
): Promise<GuestJoinStartResponse> =>
  post(`${GUEST_JOIN_BASE_PATH}/start`, { token });

export const sendGuestJoinCode = (
  token: string,
): Promise<GuestJoinSendCodeResponse> =>
  post(`${GUEST_JOIN_BASE_PATH}/send-code`, { token });

export const verifyGuestOtp = (
  challengeToken: string,
  code: string,
): Promise<GuestJoinVerifyResponse> =>
  post(`${GUEST_JOIN_BASE_PATH}/verify`, {
    challenge_token: challengeToken,
    code,
  });

export const resendGuestOtp = (
  challengeToken: string,
): Promise<GuestJoinResendResponse> =>
  post(`${GUEST_JOIN_BASE_PATH}/resend`, { challenge_token: challengeToken });

export const getGuestVideoToken = (
  guestSessionToken: string,
): Promise<GuestJoinTokenResponse> =>
  post(`${GUEST_JOIN_BASE_PATH}/token`, {
    guest_session_token: guestSessionToken,
  });
